import apiClient from '../../../../lib/axios'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import type {
  AttendanceOverviewData,
  AttendanceRecord,
  AttendanceSummary,
  PendingRequest,
  PendingRequestsQuery,
  PendingRequestsResponse,
  TodayAttendanceQuery,
  TodayAttendanceResponse,
} from '../types/hrmsAttendanceLeave.types'

/**
 * GET /api/hrms/attendance/overview/summary?date=...
 * Loads aggregated branch-wide metrics
 */
export async function getAttendanceOverviewSummary(date: string): Promise<AttendanceSummary> {
  const params: Record<string, any> = {}
  if (date?.trim()) params.date = date.trim()

  try {
    const response = await apiClient.get<any>(ENDPOINTS.HRMS.ATTENDANCE_OVERVIEW_SUMMARY, {
      params,
    })
    const payload = response.data?.data ?? response.data ?? {}

    const totalStaff = Number(payload.totalStaff ?? payload.totalEmployees ?? payload.totalCount ?? payload.total ?? 0)
    const presentToday = Number(payload.presentToday ?? payload.presentCount ?? payload.present ?? 0)
    const onLeaveToday = Number(payload.onLeaveToday ?? payload.onLeave ?? payload.leaveCount ?? 0)
    const pendingRequests = Number(payload.pendingRequests ?? payload.pendingCount ?? payload.pending ?? 0)
    const presentPercentage =
      typeof payload.presentPercentage === 'number'
        ? payload.presentPercentage
        : totalStaff > 0
        ? Math.round((presentToday / totalStaff) * 1000) / 10
        : 0

    return {
      totalStaff,
      presentToday,
      presentPercentage,
      onLeaveToday,
      pendingRequests,
    }
  } catch (error) {
    console.warn(`Failed to fetch attendance overview summary for date ${date}:`, error)
    return {
      totalStaff: 0,
      presentToday: 0,
      presentPercentage: 0,
      onLeaveToday: 0,
      pendingRequests: 0,
    }
  }
}

/**
 * GET /api/hrms/attendance/overview/today
 * Populates Today's Attendance quick-view table
 */
export async function getTodayAttendance(
  query: TodayAttendanceQuery
): Promise<TodayAttendanceResponse> {
  const params: Record<string, any> = {}
  if (query.date?.trim()) params.date = query.date.trim()
  if (query.departmentId?.trim() && query.departmentId !== 'All' && query.departmentId !== 'All Departments') {
    params.departmentId = query.departmentId.trim()
  }
  if (query.status?.trim() && query.status !== 'ALL' && query.status !== 'All') {
    params.status = query.status.trim()
  }
  if (query.search?.trim()) {
    params.search = query.search.trim()
  }
  if (query.page) params.page = query.page
  if (query.limit) params.limit = query.limit

  try {
    const response = await apiClient.get<any>(ENDPOINTS.HRMS.ATTENDANCE_OVERVIEW_TODAY, {
      params,
    })
    const payload = response.data?.data ?? response.data

    const items: any[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.attendance)
      ? payload.attendance
      : []

    const total = Number(payload?.total ?? payload?.totalCount ?? items.length)

    return {
      items: items.map((rec, idx) => ({
        id: String(rec.id ?? rec.attendanceId ?? rec._id ?? `att-${idx}`),
        name: String(
          (rec.name ??
          rec.employeeName ??
          rec.fullName ??
          (rec.staffProfile ? `${rec.staffProfile.firstName || ''} ${rec.staffProfile.lastName || ''}`.trim() : '')) ||
          'Staff Member'
        ),
        employeeId: String(rec.employeeId ?? rec.empId ?? rec.staffProfile?.employeeId ?? '—'),
        department: String(
          rec.departmentName ??
          (typeof rec.department === 'object' && rec.department !== null ? rec.department.name : rec.department) ??
          rec.staffProfile?.department?.name ??
          rec.staffProfile?.department ??
          'General'
        ),
        checkIn: rec.checkIn ?? rec.punchIn ?? rec.firstPunch ?? '—',
        checkOut: rec.checkOut ?? rec.punchOut ?? rec.lastPunch ?? '—',
        workingMinutes: Number(rec.workingMinutes ?? rec.durationMinutes ?? 0),
        status: rec.status ?? 'Present',
        lateMinutes: Number(rec.lateMinutes ?? 0),
      })),
      total,
      page: Number(payload?.page ?? query.page ?? 1),
      limit: Number(payload?.limit ?? query.limit ?? 10),
    }
  } catch (error) {
    console.warn(`Failed to fetch today's attendance for date ${query.date}:`, error)
    return { items: [], total: 0, page: query.page ?? 1, limit: query.limit ?? 10 }
  }
}

/**
 * GET /api/hrms/attendance/overview/pending-requests
 * Unified pending requests queue
 */
export async function getAttendancePendingRequests(
  query: PendingRequestsQuery
): Promise<PendingRequestsResponse> {
  const params: Record<string, any> = {}
  if (query.requestType?.trim() && query.requestType !== 'ALL' && query.requestType !== 'All') {
    params.requestType = query.requestType.trim()
  }
  if (query.departmentId?.trim() && query.departmentId !== 'All' && query.departmentId !== 'All Departments') {
    params.departmentId = query.departmentId.trim()
  }
  if (query.search?.trim()) {
    params.search = query.search.trim()
  }
  if (query.date?.trim()) {
    params.date = query.date.trim()
  }
  if (query.page) params.page = query.page
  if (query.limit) params.limit = query.limit

  try {
    const response = await apiClient.get<any>(ENDPOINTS.HRMS.ATTENDANCE_OVERVIEW_PENDING_REQUESTS, {
      params,
    })
    const payload = response.data?.data ?? response.data

    const items: any[] = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload?.pendingRequests)
      ? payload.pendingRequests
      : []

    const total = Number(payload?.total ?? payload?.totalCount ?? items.length)

    return {
      items: items.map((req, idx) => ({
        id: String(req.id ?? req.requestId ?? req._id ?? `req-${idx}`),
        name: String(
          (req.name ??
          req.employeeName ??
          req.fullName ??
          (req.staffProfile ? `${req.staffProfile.firstName || ''} ${req.staffProfile.lastName || ''}`.trim() : '')) ||
          'Staff Member'
        ),
        employeeId: String(req.employeeId ?? req.empId ?? req.staffProfile?.employeeId ?? '—'),
        department: String(
          req.departmentName ??
          (typeof req.department === 'object' && req.department !== null ? req.department.name : req.department) ??
          req.staffProfile?.department?.name ??
          req.staffProfile?.department ??
          'General'
        ),
        requestType: req.requestType ?? req.type ?? (req.leaveType ? 'Leave Request' : 'Regularization'),
        title: String(req.title ?? req.subject ?? req.leaveType ?? 'Attendance Request'),
        detail: String(req.detail ?? req.reason ?? req.description ?? 'Pending approval'),
        submittedAt: String(req.submittedAt ?? req.createdAt ?? new Date().toISOString()),
        status: req.status ?? 'Pending',
      })),
      total,
      page: Number(payload?.page ?? query.page ?? 1),
      limit: Number(payload?.limit ?? query.limit ?? 10),
    }
  } catch (error) {
    console.warn('Failed to fetch attendance pending requests:', error)
    return { items: [], total: 0, page: query.page ?? 1, limit: query.limit ?? 10 }
  }
}

/**
 * Aggregated helper (supports backward compatibility)
 */
export async function getAttendanceOverview(
  date: string,
  departmentId?: string
): Promise<AttendanceOverviewData> {
  const [summary, todayRes, pendingRes] = await Promise.all([
    getAttendanceOverviewSummary(date),
    getTodayAttendance({ date, departmentId, page: 1, limit: 10 }),
    getAttendancePendingRequests({ departmentId, page: 1, limit: 10 }),
  ])

  return {
    summary,
    attendance: todayRes.items,
    pendingRequests: pendingRes.items,
  }
}
